import { ArrowRight, Flame, Scale, Sparkles } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { Entry, UsualMeal } from '../../shared/api';
import { addDays, friendlyDay, longDate, MEAL_LABELS, mealForHour, type Meal } from '../../shared/dates';
import { formatWeight } from '../../shared/units';
import { useCopyEntries, useDay, useSettings, useUsual, useWeights } from '../api/hooks';
import { Composer } from '../components/Composer';
import { EntrySheet } from '../components/EntrySheet';
import { useLogItems } from '../components/log/useLogItems';
import { MealSection } from '../components/MealSection';
import { MacroBars, NutrientChips } from '../components/NutrientProgress';
import { Ring } from '../components/Ring';
import { WeekStrip } from '../components/WeekStrip';
import { kcal } from '../lib/format';
import { localHour } from '../lib/today';
import { useUI } from '../lib/ui';

export function TodayPage() {
  const { today, selectedDate, setSelectedDate, openLog, openWhatFits, openWeighIn, toast } = useUI();
  const day = useDay(selectedDate, today);
  const settings = useSettings();
  const weights = useWeights('30', today);
  const copy = useCopyEntries();
  const isToday = selectedDate === today;
  const nextMeal = mealForHour(localHour());
  const usual = useUsual(selectedDate, nextMeal);
  const { log } = useLogItems(selectedDate, nextMeal);
  const [editing, setEditing] = useState<Entry | null>(null);
  const defs = useMemo(() => new Map((settings.data?.nutrients ?? []).map((n) => [n.key, n])), [settings.data]);
  const aiEnabled = settings.data?.ai.enabled ?? false;
  const unit = settings.data?.profile.weightUnit ?? 'lb';

  const data = day.data;
  const goal = data?.targets.find((t) => t.key === 'kcal')?.amount ?? 0;
  const eaten = data?.totals.kcal ?? 0;
  const left = goal - eaten;
  const proteinTarget = data?.targets.find((t) => t.key === 'protein')?.amount ?? 0;
  const proteinLeft = Math.max(0, proteinTarget - (data?.totals.protein ?? 0));
  const summary = weights.data?.summary;

  const copying = useRef(false);
  const copyYesterday = (meal: Meal) => {
    if (copying.current) return; // a double tap must not copy twice
    copying.current = true;
    copy.mutate(
      { fromDate: addDays(selectedDate, -1), toDate: selectedDate, meal },
      {
        onSuccess: (created) => toast(`Copied ${created.length} ${created.length === 1 ? 'item' : 'items'} from yesterday`),
        onError: (e) => toast(e.message, { tone: 'error' }),
        onSettled: () => {
          copying.current = false;
        },
      },
    );
  };

  const logUsual = (u: UsualMeal) =>
    void log(
      u.items.map((i) => ({ name: i.name, brand: i.brand, foodRef: i.foodRef, grams: i.grams, per100: i.per100, servings: [], source: 'usual' as const })),
    );

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">{friendlyDay(selectedDate, today)}</h1>
          <p className="page__sub">{longDate(selectedDate)}</p>
        </div>
        <div className="row wrap" style={{ justifyContent: 'flex-end' }}>
          {settings.data?.ai.provider === 'stub' && (
            <span className="chip chip--ai" title="AI answers are simulated offline from your data — no keys or network needed">
              <Sparkles size={14} /> Demo AI
            </span>
          )}
          {data && data.streak > 0 && (
            <span className="chip chip--accent" title="Days in a row with at least one entry">
              <Flame size={15} /> {data.streak}-day streak
            </span>
          )}
        </div>
      </div>

      <WeekStrip selected={selectedDate} today={today} onSelect={setSelectedDate} />

      <section className="card card--pad" aria-label="Daily budget">
        {day.isLoading || !data ? (
          <div className="budget">
            <div className="skeleton" style={{ width: 148, height: 148, borderRadius: '50%' }} />
            <div className="stack">
              <div className="skeleton" style={{ height: 22 }} />
              <div className="skeleton" style={{ height: 22 }} />
              <div className="skeleton" style={{ height: 22 }} />
            </div>
          </div>
        ) : (
          <>
            <div className="budget">
              <Ring eaten={eaten} goal={goal} />
              <div className="stack" style={{ gap: 14 }}>
                <div className="row between small">
                  <span className="muted">
                    Eaten <b className="num" style={{ color: 'var(--ink)' }}>{kcal(eaten)}</b>
                  </span>
                  <span className="muted">
                    Goal <b className="num" style={{ color: 'var(--ink)' }}>{kcal(goal)}</b>
                  </span>
                </div>
                <MacroBars day={data} defs={defs} />
              </div>
            </div>
            <NutrientChips day={data} defs={defs} />
          </>
        )}
      </section>

      <Composer />

      {isToday && aiEnabled && data && left > 150 && (
        <button className="card card--pad card--ai row" style={{ textAlign: 'left', width: '100%', cursor: 'pointer' }} onClick={() => openWhatFits({ meal: nextMeal })}>
          <span className="coach__avatar" aria-hidden="true">
            <Sparkles size={18} />
          </span>
          <span className="grow">
            <span style={{ display: 'block', fontWeight: 650 }}>What fits for {MEAL_LABELS[nextMeal].toLowerCase()}?</span>
            <span className="small muted num">
              {kcal(left)} kcal{proteinLeft > 5 ? ` and ${Math.round(proteinLeft)} g protein` : ''} left. Get ideas from foods you like.
            </span>
          </span>
          <ArrowRight size={18} className="muted" />
        </button>
      )}

      <section className="card card--pad" aria-label="Meals" style={{ paddingTop: 8, paddingBottom: 8 }}>
        {data?.meals.map((m) => (
          <MealSection
            key={m.meal}
            view={m}
            yesterdayCount={data.yesterday[m.meal]}
            usual={isToday && m.meal === nextMeal ? usual.data?.[0] : undefined}
            onAdd={() => openLog({ meal: m.meal, date: selectedDate })}
            onOpenEntry={setEditing}
            onLogUsual={logUsual}
            onCopyYesterday={() => copyYesterday(m.meal)}
          />
        ))}
        {!data && [0, 1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ height: 48, margin: '10px 0' }} />)}
      </section>

      <section className="card card--pad row" aria-label="Weight">
        <span className="meal__icon" style={{ background: 'var(--surface-2)' }} aria-hidden="true">
          <Scale size={18} />
        </span>
        <div className="grow">
          <div style={{ fontWeight: 650 }}>
            {summary?.trendKg != null ? `Trend ${formatWeight(summary.trendKg, unit)}` : 'Weight'}
          </div>
          <div className="small muted num">
            {summary?.rate
              ? `${formatWeight(summary.rate.kgPerWeek, unit, { signed: true })} per week`
              : 'Weigh in a few times a week to see your trend.'}
            {summary?.latestDate === today ? ' · weighed in today' : ''}
          </div>
        </div>
        <Link to="/progress" className="btn btn--quiet btn--sm">
          Details
        </Link>
        <button className="btn btn--accent btn--sm" onClick={() => openWeighIn()}>
          Weigh in
        </button>
      </section>

      <Composer floating />
      {editing && <EntrySheet entry={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
